import { ApiError, useCreateFacilitiesMutation, useUpdateFacilitiesMutation } from '@schooly/api';
import { useInvalidateListQueriesFor } from '@schooly/components/filters';
import { useNotifications } from '@schooly/components/notifications';
import { useFacilities } from '@schooly/hooks/use-facilities';
import { Loading, ModalSmall } from '@schooly/style';
import { FC, useCallback } from 'react';

import { useRouter } from '../../../../../context/router/useRouter';
import { useSchool } from '../../../../../hooks/useSchool';
import { SchoolSettingsLayout } from '../../../SchoolSettingsLayout';
import {
  FacilitiesForm,
  SchoolGeneralFacilitiesModalContent,
} from './SchoolGeneralFacilitiesModalContent';
import { formItemToCreateFacilityItem } from './utils';

export const SchoolGeneralFacilitiesModal: FC = () => {
  const { goBack } = useRouter();
  const { schoolId = '' } = useSchool();
  const { showError, showNotification } = useNotifications();
  const invalidateQueries = useInvalidateListQueriesFor('facility');

  const { facilities, isFetching } = useFacilities(
    { schoolId },
    { enabled: !!schoolId, refetchOnMount: 'always' },
  );

  const emptyFacilities = facilities.length === 0;
  const createFacilities = useCreateFacilitiesMutation();
  const updateFacilities = useUpdateFacilitiesMutation();

  const handleSave = useCallback(
    async (data: FacilitiesForm) => {
      if (!schoolId) return;

      const facilities = data.facilities.map(formItemToCreateFacilityItem);

      try {
        if (emptyFacilities) {
          await createFacilities.mutateAsync({ school_id: schoolId, facilities });
        } else {
          await updateFacilities.mutateAsync({ params: { schoolId }, data: { facilities } });
        }
        showNotification({ textId: 'school-tabs-Facilities-SaveSuccess', type: 'success' });
        invalidateQueries();
        goBack();
      } catch (e) {
        showError(e as ApiError);
      }
    },
    [
      schoolId,
      emptyFacilities,
      showNotification,
      invalidateQueries,
      goBack,
      createFacilities,
      updateFacilities,
      showError,
    ],
  );

  return (
    <SchoolSettingsLayout>
      {isFetching ? (
        <ModalSmall open onClose={goBack}>
          <Loading />
        </ModalSmall>
      ) : (
        <SchoolGeneralFacilitiesModalContent
          facilities={facilities}
          onSubmit={handleSave}
          isSaving={createFacilities.isLoading || updateFacilities.isLoading}
        />
      )}
    </SchoolSettingsLayout>
  );
};
